import React, { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Badge, Button, Input, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { approvalsApi, dcCustomersApi, customerRolesApi } from "../../../helpers/dck_helper";
import { useResourceList, useBackendList } from "../../../query/useResource";
import {
  CUSTOMER_LOOKUP_PARAMS,
  ROLE_LOOKUP_PARAMS,
  managerOptionFromCustomer,
  customerPersonView,
  lookupLoadError,
} from "../../../helpers/lookupOptions";
import { useApproverRoles } from "../../../query/useApproverRoles";
import { AsyncBoundary } from "../StatusStates";
import { useRbac } from "../RbacGuard";
import { useSessionStore } from "../../../stores/sessionStore";
import { APPROVE_MESSAGES, REJECT_MESSAGES } from "./cannedMessages";
import StatusTag from "../ui/StatusTag";
import { Alert } from "../ui";
// 直接指到檔案而不是 barrel:barrel(../index)也 re-export 本檔,繞回來會是循環相依
import { ConfirmDialog } from "../ConfirmDialog";

// 審核狀態 → StatusTag 語意鍵(Figma tag 變體)
const STATUS_TAG_KEY = { pending: "pending", approved: "approved", rejected: "rejected", draft: "draft" };

// 單據編號格式（與審核中心一致）
/**
 * 單據編號(260804 稿:日期制 RQ-YYYYMMDD-NNN)。
 * 傳 approval 物件(帶 createdAt)可組完整日期;傳純 id 落回舊零補格式
 * (僅存於沒有日期脈絡的舊呼叫,應逐步淘汰)。
 */
export const formatDocNo = (approvalOrId) => {
  if (approvalOrId && typeof approvalOrId === "object") {
    const { id, createdAt } = approvalOrId;
    if (createdAt) {
      const d = new Date(createdAt);
      const pad = (n) => String(n).padStart(2, "0");
      return `RQ-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${String(id % 1000).padStart(3, "0")}`;
    }
    return `RQ-${String(id).padStart(6, "0")}`;
  }
  return `RQ-${String(approvalOrId).padStart(6, "0")}`;
};

/**
 * 三關審核引擎前端（主計劃 P0-3）。
 *
 * 掛在幾乎所有 CRUD 與批次匯入之上：發動→審核→主管，同一人自動 Pass。
 * 後端 mock 已實作關卡推進與審核歷程；前端提供送審、狀態列、歷程、審核操作。
 *
 * 元件：
 *   <SubmitForApprovalButton resource recordId action summary onSubmitted/>
 *   <ApprovalStatusBadge status/>  <ApprovalProgress approval/>
 *   <ApprovalHistory history/>     <ApprovalActions approval onActed/>
 * Hook：
 *   useApproval() → { submit, approve, reject }
 */

const STAGE_LABEL = { submit: "發動", review: "審核", manager: "主管" };
const STATUS_META = {
  pending: { color: "warning", text: "審核中" },
  approved: { color: "success", text: "已通過" },
  rejected: { color: "danger", text: "已退回" },
  draft: { color: "secondary", text: "草稿" },
};

export function useApproval() {
  const { role } = useRbac();
  const sessionUser = useSessionStore((s) => s.user);
  const currentUser = sessionUser
    ? `${sessionUser.name}${sessionUser.employeeId ? `(${sessionUser.employeeId})` : ""}`
    : "current-user";

  const submit = useCallback(
    ({ resource, recordId, action = "create", summary = "", reviewer, manager, payload }) =>
      approvalsApi.submit({ resource, recordId, action, summary, by: currentUser, role, reviewer, manager, payload }),
    [role, currentUser]
  );
  const approve = useCallback(
    (id, message) => approvalsApi.approve(id, { by: currentUser, message }),
    [currentUser]
  );
  const reject = useCallback(
    (id, message) => approvalsApi.reject(id, { by: currentUser, message }),
    [currentUser]
  );
  // 撤回:只對還在審核中的單,後端會擋非發動者(ticket 06)
  const withdraw = useCallback(
    (id, message) => approvalsApi.withdraw(id, { by: currentUser, message }),
    [currentUser]
  );
  return { submit, approve, reject, withdraw, currentUser };
}

/**
 * 某筆資料「目前那張單」與「我是不是發動者」（ticket 06）。
 *
 * 撤回只有發動者本人能按 —— 後端會擋，但按鈕本來就不該出現給按不動的人。
 * 詳情頁要判斷這件事就得知道現在那張單是誰發的，所以收成一顆 hook 共用。
 */
export function useRecordApproval(resource, recordId) {
  const sessionUser = useSessionStore((s) => s.user);
  const currentUser = sessionUser
    ? `${sessionUser.name}${sessionUser.employeeId ? `(${sessionUser.employeeId})` : ""}`
    : "current-user";
  const q = useResourceList(approvalsApi, "approvals", { resource, recordId });
  const current = currentApproval(q.data?.data ?? []);
  return {
    current,
    isInitiator: !!current && current.initiator === currentUser,
    refetch: q.refetch,
  };
}

export const ApprovalStatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.draft;
  // 樣式走共用 StatusTag（模板 badge-soft pill）
  return <StatusTag status={STATUS_TAG_KEY[status] || "draft"}>{meta.text}</StatusTag>;
};
ApprovalStatusBadge.propTypes = { status: PropTypes.string };

// 關卡進度條：發動 →(審核)→(主管)
// 資料本身的狀態 → StatusTag 變體
const ENTITY_STATUS_VARIANT = {
  // 機櫃/使用者已收斂為「啟用中」(260804 稿;issue 06)
  // 生效中/進行中=營運資訊(合約/專案)的 active 語彙,同一套「啟用=綠」的色階
  啟用中: "success", 運行中: "success",
  生效中: "success", 進行中: "success",
  維修中: "warning", 暫停: "warning", 移轉中: "warning",
  待汰換: "error", 離線: "error", 已到期: "error",
  已結案: "gary", 停用: "gary", 閒置: "gary", 已終止: "gary",
};

/**
 * 狀態欄（Figma DCK-05 機櫃 86:19556）。
 *
 * 設計稿把「狀態」與「審核」合併成同一欄:送審中的列顯示
 * 「審核中｜單據編號」並連到審核中心(整列另有顏色註記底色),
 * 其餘顯示資料本身的狀態。
 */
export const StatusCell = ({ status, approvalStatus, docNo, lifecycle }) => {
  if (approvalStatus === "pending") {
    return (
      <Link to="/dck/approvals?status=pending" className="text-reset">
        <StatusTag variant="warning">{docNo ? `審核中｜${docNo}` : "審核中"}</StatusTag>
      </Link>
    );
  }
  if (approvalStatus === "rejected") return <StatusTag variant="error">已退回</StatusTag>;
  // 還沒實體上架的機器,顯示它卡在哪個階段而不是設備狀態 ——
  // 一台還沒進機房的機器說「運行中」會讓人以為它可以用了(ticket 01)。
  if (lifecycle === "reserved") return <StatusTag status="reserved" />;
  return <StatusTag variant={ENTITY_STATUS_VARIANT[status] || "gary"}>{status || "—"}</StatusTag>;
};
StatusCell.propTypes = {
  status: PropTypes.string,
  approvalStatus: PropTypes.string,
  docNo: PropTypes.string,
  /** 硬體的上架階段;沒給就跟以前一樣只看設備狀態 */
  lifecycle: PropTypes.string,
};

/** 送審中(鎖定)→ 整列顏色註記;給 <DataTable rowClassName> 用 */
export const pendingRowClass = (row) => (row.approvalStatus === "pending" ? "dck-row-pending" : "");

/**
 * 這筆資料現在能不能改:送審中或已結案都不行。
 * 列表操作欄與詳情頁動作共用同一條規則,免得兩邊各判一次而漂移。
 */
export const isFrozen = (row) =>
  !!row && (row.approvalStatus === "pending" || row.status === "已結案");

export const ApprovalProgress = ({ approval }) => {
  if (!approval) return null;
  const stages = ["submit", "review", "manager"];
  const done = new Set((approval.history || []).map((h) => h.stage));
  const isApproved = approval.status === "approved";
  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {stages.map((s, i) => {
        const passed = done.has(s) || (isApproved);
        const active = approval.pendingStage === s;
        return (
          <React.Fragment key={s}>
            {i > 0 && <i className="mdi mdi-chevron-right text-muted" />}
            <Badge color={active ? "info" : passed ? "success" : "light"} className={passed || active ? "" : "text-muted"}>
              {STAGE_LABEL[s]}
            </Badge>
          </React.Fragment>
        );
      })}
    </div>
  );
};
ApprovalProgress.propTypes = { approval: PropTypes.object };

// 審核歷程（時間軸）
export const ApprovalHistory = ({ history = [] }) => (
  <ul className="list-unstyled mb-0">
    {history.map((h, i) => (
      <li key={i} className="d-flex gap-2 py-1 border-bottom">
        <Badge color={h.action === "reject" ? "danger" : h.action === "submit" ? "secondary" : "success"}>
          {STAGE_LABEL[h.stage] || h.stage}
        </Badge>
        <span className="flex-grow-1">
          {h.message}
          {h.auto ? <span className="text-muted small">（自動）</span> : null}
        </span>
        <span className="text-muted small">{h.by}</span>
        <span className="text-muted small">{new Date(h.at).toLocaleString("zh-TW")}</span>
      </li>
    ))}
  </ul>
);
ApprovalHistory.propTypes = { history: PropTypes.array };

// 送審按鈕（含確認 + 罐頭訊息）
export const SubmitForApprovalButton = ({
  resource, recordId, action = "create", summary = "",
  label = "送出審核", color = "primary", onSubmitted,
}) => {
  const { submit } = useApproval();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(summary);
  const [busy, setBusy] = useState(false);

  const doSubmit = async () => {
    setBusy(true);
    try {
      const res = await submit({ resource, recordId, action, summary: note });
      setOpen(false);
      if (onSubmitted) onSubmitted(res);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button color={color} onClick={() => setOpen(true)}>
        <i className="mdi mdi-send-check-outline me-1" />{label}
      </Button>
      <Modal isOpen={open} toggle={() => setOpen(false)} centered>
        <ModalHeader toggle={() => setOpen(false)}>送出審核</ModalHeader>
        <ModalBody>
          <p className="text-muted">此操作將整批送三關審核（發動→審核→主管）。</p>
          <Input
            type="textarea" rows={3} placeholder="送審說明（可選）"
            value={note} onChange={(e) => setNote(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setOpen(false)}>取消</Button>
          <Button color="primary" disabled={busy} onClick={doSubmit}>確認送出</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
SubmitForApprovalButton.propTypes = {
  resource: PropTypes.string,
  recordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  action: PropTypes.string,
  summary: PropTypes.string,
  label: PropTypes.string,
  color: PropTypes.string,
  onSubmitted: PropTypes.func,
};

// 審核操作（核准/退回 + 罐頭訊息）
export const ApprovalActions = ({ approval, onActed }) => {
  const { approve, reject } = useApproval();
  const { can } = useRbac();
  const [mode, setMode] = useState(null); // 'approve' | 'reject'
  const [canned, setCanned] = useState("");
  const [note, setNote] = useState(""); // 補充說明（選填,最多 100 字）
  const [busy, setBusy] = useState(false);

  // 沒有審核權(如 Viewer)只看得到歷程,不給同意/不同意
  if (!approval || approval.status !== "pending" || !can("approval.update")) return null;
  const options = mode === "reject" ? REJECT_MESSAGES : APPROVE_MESSAGES;
  const start = (m) => { setMode(m); setCanned((m === "reject" ? REJECT_MESSAGES : APPROVE_MESSAGES)[0]); setNote(""); };
  const reset = () => { setMode(null); setNote(""); };

  const act = async () => {
    const message = note.trim() ? `${canned}（${note.trim()}）` : canned;
    setBusy(true);
    try {
      const res = mode === "reject"
        ? await reject(approval.id, message)
        : await approve(approval.id, message);
      reset();
      if (onActed) onActed(res);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="d-flex gap-2">
        <Button color="success" onClick={() => start("approve")}>
          <i className="mdi mdi-check me-1" />同意
        </Button>
        <Button color="danger" outline onClick={() => start("reject")}>
          <i className="mdi mdi-close me-1" />不同意
        </Button>
      </div>
      {mode && (
        <div className="mt-2">
          <label className="form-label small mb-1">
            {mode === "reject" ? "退回原因" : "罐頭訊息"} <span className="text-danger">＊</span>
          </label>
          <Input type="select" className="mb-2" value={canned} onChange={(e) => setCanned(e.target.value)}>
            {options.map((m) => <option key={m} value={m}>{m}</option>)}
          </Input>
          <label className="form-label small mb-1">補充說明（選填）</label>
          <Input type="textarea" rows={2} maxLength={100} className="mb-2"
            placeholder="最多 100 字" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="d-flex gap-2">
            <Button size="sm" color={mode === "reject" ? "danger" : "success"} disabled={busy} onClick={act}>
              確認{mode === "reject" ? "不同意" : "同意"}
            </Button>
            <Button size="sm" color="light" onClick={reset}>取消</Button>
          </div>
        </div>
      )}
    </div>
  );
};
ApprovalActions.propTypes = { approval: PropTypes.object, onActed: PropTypes.func };

// 選擇審核對象（送審 dialog）— 指定第二關審核者(必) + 第三關主管核決(選填)
// 版面依 Figma DCK 審核流程(52:13457)。
/**
 * 送審選人這一步**這個月先隱藏**（2026-08-19 使用者指示）。
 *
 * 8 月的重點是「匯入的資料呈現得出來、查得到、搜尋得到」，以及新增／刪除能走完；
 * 審核是下一 round。而在後端接手審核之前，這條路是**壞的**：改動只存在審核單的
 * payload 裡，核准後由 mock 的審核引擎寫回 mock store，而頁面讀的是真後端 ——
 * 兩邊不是同一份資料，所以「送審 → 核准 → 資料沒變」（票 39）。
 *
 * 所以關掉之後不是「少一步」，而是**寫入真的會發生**：確認 → 直接寫後端。
 *
 * 要把審核開回來：設 `REACT_APP_APPROVAL_STEP=on`。開回來之前請先確認
 * 審核引擎寫得到真後端，否則就是回到票 39 的狀態。
 */
// 讀成函式而不是模組常數:模組常數在 import 當下就定死,測試沒有辦法在同一個
// 檔案裡同時涵蓋「開」與「關」兩條路 —— 而這兩條路都要有測試守著。
export const approvalStepEnabled = () => process.env.REACT_APP_APPROVAL_STEP === "on";

/** action → 確認彈窗的標題與按鈕（畫面其他內容不動，只是把選人那段換掉） */
const CONFIRM_META = {
  create: { title: "確認新增", confirmText: "確認，送出", color: "primary" },
  update: { title: "確認異動", confirmText: "確認，送出", color: "primary" },
  delete: { title: "確認刪除", confirmText: "確認，刪除", color: "danger" },
  close: { title: "確認結案", confirmText: "確認，送出", color: "primary" },
  install: { title: "確認上架", confirmText: "確認，送出", color: "primary" },
  withdraw: { title: "確認撤回", confirmText: "確認，撤回", color: "danger" },
};

/**
 * 「確認新增／異動／刪除」—— 審核選人隱藏時走的那條路。
 *
 * `onConfirm` 是**真正的寫入**（呼叫端自己知道要打哪支 API）。沒有給的話退回
 * 舊的送審行為，這樣還沒搬過來的頁面不會靜靜地變成「按了沒事」。
 */
const ConfirmActionModal = ({
  isOpen, toggle, resource, recordId, action = "create", summary = "", payload,
  onConfirm, onSubmitted,
}) => {
  const { submit } = useApproval();
  const meta = CONFIRM_META[action] || { title: "確認送出", confirmText: "確認，送出", color: "primary" };
  const doConfirm = async () => {
    if (onConfirm) return onConfirm();
    // 尚未搬過來的頁面:維持原本的送審(不指定審核者),行為與之前一致
    const res = await submit({ resource, recordId, action, summary, payload });
    if (onSubmitted) onSubmitted(res);
    return res;
  };
  return (
    <ConfirmDialog
      isOpen={!!isOpen}
      toggle={toggle}
      title={meta.title}
      message={summary}
      confirmText={meta.confirmText}
      confirmColor={meta.color}
      onConfirm={doConfirm}
    />
  );
};
ConfirmActionModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  resource: PropTypes.string,
  recordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  action: PropTypes.string,
  summary: PropTypes.string,
  payload: PropTypes.object,
  onConfirm: PropTypes.func,
  onSubmitted: PropTypes.func,
};

const ApproverPicker = ({
  isOpen, toggle, resource, recordId, action = "create", summary = "", payload, onSubmitted,
}) => {
  const { submit } = useApproval();
  const sessionUser = useSessionStore((s) => s.user);
  const initiator = sessionUser
    ? `${sessionUser.name}${sessionUser.employeeId ? `(${sessionUser.employeeId})` : ""}`
    : "current-user";

  const peopleQ = useBackendList(dcCustomersApi, "dcCustomersOptions", CUSTOMER_LOOKUP_PARAMS);
  const rolesQ = useBackendList(customerRolesApi, "customerRolesLookup", ROLE_LOOKUP_PARAMS);
  const [reviewer, setReviewer] = useState("");
  const [manager, setManager] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const users = useMemo(
    () => (peopleQ.data?.items || []).map(customerPersonView).filter(Boolean),
    [peopleQ.data]
  );
  const roles = rolesQ.data?.items ?? [];
  const approverRoles = useApproverRoles(roles, users.map((u) => u.roleId));
  const rolesReady = !rolesQ.isLoading && !rolesQ.error && !approverRoles.isLoading;
  const nameOf = (u) => managerOptionFromCustomer({
    id: u.id, name: u.name, employee_no: u.employee_no || u.employeeId,
  })?.label;
  // 候選＝**權限設定裡「審核功能（三關簽核）」設為可編輯**的角色底下的人（票 38）。
  //
  // 原本寫死「代碼是 Gatekeeper／Overseer」。但角色是使用者自己新增、權限自己設的
  // （ADR-0002 補述三）—— 客戶建的「維運主管」永遠選不到,而 testenv 上四個人全掛在
  // 零權限的 `Registered`,清單就整個是空的。改看能力之後,權限怎麼設、這裡就怎麼算。
  //
  // ⚠️ 第三關（主管核決）與第二關**共用同一個候選池**:權限設定只有「審核功能」一列,
  // 沒有第二關／第三關之分,前端無從再細分。要分的話得由後端在角色上多一個旗標
  // （票 38 已記）。在那之前由送審者自己挑,同一人的關卡本來就會自動 Pass。
  // 角色／modules 還沒回來時不要拿空表去查,否則每人都會誤報「不能審核」
  const reviewerCands = rolesReady
    ? users.filter((u) => approverRoles.canApprove(u.roleId))
    : [];
  const managerCands = reviewerCands;
  const peopleError = lookupLoadError("人員", peopleQ.error);
  const rolesError = lookupLoadError("角色", rolesQ.error || approverRoles.error);

  useEffect(() => {
    if (peopleQ.isLoading || rolesQ.isLoading || peopleQ.error || rolesQ.error) return;
    if (!rolesReady) return;
    if (users.length > 0 && reviewerCands.length === 0) {
      console.warn(
        "[lookup] DcCustomer 有人,但沒有任何角色把「審核功能（三關簽核）」設為可編輯 ——",
        "送審彈窗的審核者清單會是空的。請到 人員管理 › 權限設定 設定,",
        "或對照 helpers/approvalMeta.roleCanApprove / components/DCKeeper/ApprovalFlow"
      );
    }
  }, [peopleQ.isLoading, rolesQ.isLoading, peopleQ.error, rolesQ.error, rolesReady, users.length, reviewerCands.length]);

  const close = () => { setReviewer(""); setManager(""); setErr(""); toggle(); };
  const doSubmit = async () => {
    if (!reviewer) { setErr("請指定第二關審核者。"); return; }
    setBusy(true);
    try {
      const res = await submit({ resource, recordId, action, summary, payload, reviewer, manager: manager || null });
      setReviewer(""); setManager(""); setErr("");
      if (onSubmitted) onSubmitted(res);
    } finally { setBusy(false); }
  };

  return (
    <Modal isOpen={isOpen} toggle={close} centered>
      <ModalHeader toggle={close}>選擇審核對象</ModalHeader>
      <ModalBody>
        <p className="text-muted">
          第一關＝發動者(您:{initiator});請指定後續簽核人,最多三關。
        </p>
        {peopleError ? <Alert color="danger" className="mb-3">{peopleError}</Alert> : null}
        {rolesError ? <Alert color="danger" className="mb-3">{rolesError}</Alert> : null}
        {!peopleError && !peopleQ.isLoading && peopleQ.isSuccess && users.length === 0 ? (
          <Alert color="warning" className="mb-3">目前沒有可選的人員</Alert>
        ) : null}
        {!peopleError && !rolesError && !peopleQ.isLoading && rolesReady
          && users.length > 0 && reviewerCands.length === 0 ? (
          <Alert color="warning" className="mb-3">
            目前沒有可選的審核者 —— 還沒有任何角色把「審核功能（三關簽核）」設為可編輯。
            請到「人員管理 › 權限設定」設定後再送審。
          </Alert>
        ) : null}
        <div className="mb-3">
          <label className="form-label">第二關｜審核者 <span className="text-danger">＊</span></label>
          <Input type="select" value={reviewer} onChange={(e) => { setReviewer(e.target.value); setErr(""); }}>
            <option value="">搜尋或選擇人員</option>
            {reviewerCands.map((u) => <option key={u.id} value={nameOf(u)}>{nameOf(u)}</option>)}
          </Input>
        </div>
        <div className="mb-3">
          <label className="form-label">第三關｜主管核決（選填）</label>
          <Input type="select" value={manager} onChange={(e) => setManager(e.target.value)}>
            <option value="">不指定（本單至第二關結案）</option>
            {managerCands.map((u) => <option key={u.id} value={nameOf(u)}>{nameOf(u)}</option>)}
          </Input>
        </div>
        <div className="alert bg-soft-warning text-warning border-0 mb-0 py-2" role="alert">
          <i className="mdi mdi-alert-outline me-1" />與發動者為同一人之關卡將自動 Pass。
        </div>
        {err ? <div className="text-danger small mt-2">{err}</div> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={close}>取消</Button>
        <Button color="primary" disabled={busy} onClick={doSubmit}>確認，送出審核</Button>
      </ModalFooter>
    </Modal>
  );
};
/**
 * 送審／確認彈窗的單一入口 —— 呼叫端不變，這裡決定走哪一條。
 * 審核關掉時（現況）走「確認新增／異動／刪除」，開啟時走原本的選人畫面。
 */
export const SelectApproversModal = (props) =>
  (approvalStepEnabled() ? <ApproverPicker {...props} /> : <ConfirmActionModal {...props} />);

ApproverPicker.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  resource: PropTypes.string,
  recordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  action: PropTypes.string,
  summary: PropTypes.string,
  // 這次異動的內容(目前只有轉派用:要搬哪幾筆、搬給誰)
  payload: PropTypes.object,
  onSubmitted: PropTypes.func,
};

// 已送出審核（確認 dialog）— 單據編號 + 前往審核中心
export const SubmittedModal = ({ isOpen, toggle, approval, onGoCenter }) => (
  <Modal isOpen={isOpen} toggle={toggle} centered>
    <ModalHeader toggle={toggle}>已送出審核</ModalHeader>
    <ModalBody>
      <p className="mb-2">
        單據編號 <strong>{approval ? formatDocNo(approval) : ""}</strong> 已建立,
        系統已通知第一關簽核人（站內訊息＋Email）。
      </p>
      <p className="text-muted mb-0">
        您可隨時至「審核中心 › 我發動的」查看審核進度與詳情;審核結果將主動通知您。
      </p>
    </ModalBody>
    <ModalFooter>
      <Button color="light" onClick={toggle}>關閉</Button>
      <Button color="primary" onClick={onGoCenter}>前往審核中心</Button>
    </ModalFooter>
  </Modal>
);
SubmittedModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  approval: PropTypes.object,
  onGoCenter: PropTypes.func,
};

// 審核歷程時間軸（gate-based）— 第一關發動／第二關審核／第三關主管核決
// 用於審核詳情頁;active 關卡帶 <ApprovalActions>(同意/不同意),readOnly 時只顯示歷程。
const GATE_LABEL = { submit: "第一關｜發動", review: "第二關｜審核", manager: "第三關｜主管核決" };
/** 進行中(或最近一張)的審核單:審核中的優先,否則取最新申請的那張 */
export function currentApproval(rows = []) {
  if (!rows.length) return null;
  const pending = rows.find((a) => a.status === "pending");
  if (pending) return pending;
  // 不靠 API 回傳順序,自己比時間
  return rows.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
}

/** 審核單摘要前面那顆標籤的文案(依動作),沒對到就退回用 summary */
const ACTION_LABEL = {
  create: "新增申請", update: "異動申請", delete: "刪除申請",
  close: "結案申請", install: "回報上架完成", transfer: "轉派",
};

/**
 * 某筆資料的審核歷程（唯讀）。
 *
 * 一筆資料可能經過**好幾張**單 —— 硬體的兩段式就是「新增單 → 回報上架單」,
 * 只顯示最新那張的話,前一段的歷程整個看不見(兩段式新增 ticket 04)。
 * 所以這裡由新到舊列出每一張,進行中的那張排最前面。
 * 核決一律回審核中心做,所以這裡是 readOnly。
 */
export const ApprovalOfRecord = ({ resource, recordId }) => {
  const q = useResourceList(approvalsApi, "approvals", { resource, recordId });
  const rows = q.data?.data ?? [];
  const latest = currentApproval(rows);
  // 進行中的那張排第一,其餘依申請時間由新到舊
  const ordered = latest
    ? [latest, ...rows.filter((a) => a.id !== latest.id)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))]
    : [];
  return (
    <AsyncBoundary
      loading={q.isLoading}
      error={q.error}
      onRetry={q.refetch}
      isEmpty={ordered.length === 0}
      // 這是卡片裡的一小段,用整版「查無資料」插圖太重
      empty={<div className="text-muted small">尚無審核紀錄。</div>}
    >
      {ordered.map((a, i) => (
        <div key={a.id} className={i > 0 ? "mt-4 pt-3 border-top" : ""}>
          <div className="text-muted small mb-2">
            單據編號 {formatDocNo(a)}
            {a.action ? <span className="ms-2">{ACTION_LABEL[a.action] || a.summary}</span> : null}
          </div>
          <ApprovalTimeline approval={a} readOnly />
        </div>
      ))}
    </AsyncBoundary>
  );
};
ApprovalOfRecord.propTypes = {
  resource: PropTypes.string,
  recordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const ApprovalTimeline = ({ approval, onActed, readOnly = false }) => {
  if (!approval) return null;
  const stages = approval.stages || ["review", "manager"];
  const gates = ["submit", ...stages];
  const assignee = (stage) =>
    stage === "submit" ? approval.initiator
      : stage === "review" ? approval.reviewer
        : stage === "manager" ? approval.manager : null;

  return (
    <ul className="list-unstyled mb-0">
      {gates.map((stage) => {
        const hist = (approval.history || []).find((h) => h.stage === stage);
        const isActive = approval.pendingStage === stage;
        const rejected = hist && hist.action === "reject";
        const done = hist && hist.action !== "reject";
        const icon = rejected ? "mdi-close-circle" : done ? "mdi-check-circle" : isActive ? "mdi-progress-clock" : "mdi-circle-outline";
        const color = rejected ? "danger" : done ? "success" : isActive ? "info" : "secondary";
        return (
          <li key={stage} className="d-flex gap-3 py-2 border-bottom">
            <i className={`mdi ${icon} text-${color}`} style={{ fontSize: 20 }} />
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold">{GATE_LABEL[stage] || stage}</span>
                {rejected ? <Badge color="danger">已退回</Badge>
                  : done ? <Badge color="success">已完成</Badge>
                    : isActive ? <Badge color="info">進行中</Badge>
                      : <Badge color="light" className="text-muted">待處理</Badge>}
                {hist && hist.auto ? <span className="text-muted small">自動 Pass</span> : null}
              </div>
              <div className="text-muted small">
                {assignee(stage) || "—"}
                {hist ? ` · ${new Date(hist.at).toLocaleString("zh-TW")}` : ""}
              </div>
              {hist && hist.message ? <div className="mt-1">{hist.message}</div> : null}
              {isActive && !readOnly ? (
                <div className="mt-2"><ApprovalActions approval={approval} onActed={onActed} /></div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
ApprovalTimeline.propTypes = {
  approval: PropTypes.object,
  onActed: PropTypes.func,
  // 唯讀:只看歷程,不給同意/不同意(資產詳情頁用;核決一律在審核中心)
  readOnly: PropTypes.bool,
};

export default SubmitForApprovalButton;
